import React, { useState } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, Space, message, Timeline, Empty } from 'antd';
import { BookOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useListBitacoraQuery, useAddBitacoraMutation, useDeleteBitacoraMutation, useListServersQuery, useListProjectsQuery } from '../store/api';

const { Option } = Select;
const { TextArea } = Input;

const tipoColors = { nota: 'blue', incidente: 'red', mantenimiento: 'orange', cambio: 'green', deploy: 'purple' };

const Bitacora = () => {
  const { data = [], isLoading } = useListBitacoraQuery();
  const { data: servers = [] } = useListServersQuery();
  const { data: projects = [] } = useListProjectsQuery();
  const [addEntry] = useAddBitacoraMutation();
  const [deleteEntry] = useDeleteBitacoraMutation();
  const [modal, setModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [form] = Form.useForm();

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      await addEntry(values).unwrap();
      message.success('Entrada creada'); setModal(false); form.resetFields();
    } catch { message.error('Error'); }
  };

  const columns = [
    { title: 'Fecha', dataIndex: 'created_at', render: d => dayjs(d).format('DD/MM/YYYY HH:mm'), width: 140, sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(), defaultSortOrder: 'descend' },
    { title: 'Tipo', dataIndex: 'tipo', render: t => <Tag color={tipoColors[t]}>{t}</Tag>, filters: Object.keys(tipoColors).map(k => ({ text: k, value: k })), onFilter: (v, r) => r.tipo === v },
    { title: 'Título', dataIndex: 'titulo', render: t => <strong>{t}</strong> },
    { title: 'Servidor', dataIndex: 'servidor_nombre', render: t => t || '-' },
    { title: 'Proyecto', dataIndex: 'proyecto_nombre', render: t => t || '-' },
    { title: 'Usuario', dataIndex: 'username' },
    { title: 'Contenido', dataIndex: 'contenido', ellipsis: true },
    { title: '', key: 'act', width: 50, render: (_, r) => (
      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({
        title: '¿Eliminar?', onOk: async () => { await deleteEntry(r.id).unwrap(); }
      })} />
    )},
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><BookOutlined style={{ marginRight: 8 }} />Bitácora</h1>
          <div className="page-subtitle">{data.length} entradas registradas</div>
        </div>
        <Space>
          <Select value={viewMode} onChange={setViewMode} style={{ width: 120 }}>
            <Option value="table">Tabla</Option>
            <Option value="timeline">Timeline</Option>
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModal(true); }}>Nueva Entrada</Button>
        </Space>
      </div>

      <Card bordered={false}>
        {viewMode === 'table' ? (
          <Table columns={columns} dataSource={data} rowKey="id" loading={isLoading} pagination={{ pageSize: 15 }} size="middle" scroll={{ x: true }} />
        ) : (
          data.length > 0 ? (
            <Timeline>
              {data.map(b => (
                <Timeline.Item key={b.id} color={tipoColors[b.tipo] || 'gray'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <Tag color={tipoColors[b.tipo]}>{b.tipo}</Tag>
                      <strong>{b.titulo}</strong>
                      <span style={{ color: '#8c8c8c', marginLeft: 8, fontSize: 12 }}>
                        — {b.username} • {dayjs(b.created_at).format('DD/MM/YYYY HH:mm')}
                        {b.servidor_nombre && ` • ${b.servidor_nombre}`}
                      </span>
                    </div>
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({
                      title: '¿Eliminar?', onOk: async () => { await deleteEntry(b.id).unwrap(); }
                    })} />
                  </div>
                  {b.contenido && <p style={{ marginTop: 4, color: '#595959' }}>{b.contenido}</p>}
                </Timeline.Item>
              ))}
            </Timeline>
          ) : <Empty description="Sin entradas" />
        )}
      </Card>

      <Modal title="Nueva Entrada" open={modal} onOk={handleAdd} onCancel={() => setModal(false)} okText="Guardar">
        <Form form={form} layout="vertical">
          <Form.Item name="titulo" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="tipo" label="Tipo" initialValue="nota">
            <Select>{Object.keys(tipoColors).map(t => <Option key={t} value={t}>{t}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="servidor_id" label="Servidor">
            <Select allowClear placeholder="Opcional">{servers.map(s => <Option key={s.id} value={s.id}>{s.nombre}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="proyecto_id" label="Proyecto">
            <Select allowClear placeholder="Opcional">{projects.map(p => <Option key={p.id} value={p.id}>{p.nombre}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="contenido" label="Contenido"><TextArea rows={4} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Bitacora;
